import * as React from 'react';
import type { RestrictedSingleSubject } from '../../Interfaces/RestrictedSingleSubject';
import { usePlannerContext } from '../Planner/Context/PlannerContextProvider';
import type { ElectiveGroupCode } from '../Planner/Context/types';
import type { SubjectListProps } from '../SubjectList/SubjectList';
import { SubjectList } from '../SubjectList/SubjectList';
import { SubjectListAccordion } from '../SubjectListAccordion/SubjectListAccordion';
import { creditsCalculator } from '~/Features/StudyPage/Online/Components/Utils/CreditsCalculator';
import { List } from '~/Partials/List/List';
import type { StrictOmit } from '~/Partials/Utils/typeUtils';
import styles from './SubjectsAggregator.module.css';

type ListConfig = StrictOmit<SubjectListProps, 'subjects'>;

export interface SubjectsAggregatorContent {
    subjects: RestrictedSingleSubject[];
}

export interface SubjectsAggregatorProps extends ListConfig {
    semester: string;
    obligatory: SubjectsAggregatorContent;
    elective: Map<string, SubjectsAggregatorContent>;
    onSubjectActionClick: (subject: RestrictedSingleSubject) => void;
}

export const SubjectsAggregator: React.FC<SubjectsAggregatorProps> = props => {
    const { obligatory, elective, ...restProps } = props;

    const { electiveGroupDescriptors, currentBookmark, timeDetails } = usePlannerContext();
    const { creditLimitPerElectiveGroup } = creditsCalculator(
        obligatory.subjects,
        elective,
        currentBookmark.contextKey === 'fullTime'
            ? timeDetails.fullTimeSemesterCredits
            : timeDetails.partTimeSemesterCredits
    );

    const renderElectiveGroup = (electiveGroupCode: ElectiveGroupCode, index: number) => {
        const electiveSubjects = elective.get(electiveGroupCode)?.subjects;
        const electiveGroupDescriptor = electiveGroupDescriptors.get(electiveGroupCode);

        if (!electiveSubjects || !electiveGroupDescriptor) {
            throw new Error(`Elective group '${electiveGroupCode}' is missing data`);
        }

        return (
            <li key={index} className={styles.Accordion}>
                <SubjectListAccordion
                    subjects={electiveSubjects}
                    recommendedPointLimit={creditLimitPerElectiveGroup}
                    heading={electiveGroupDescriptor.label}
                    {...restProps}
                />
            </li>
        );
    };

    return (
        <div>
            {obligatory.subjects.length !== 0 && (
                <SubjectList className={styles.Obligatory} {...restProps} {...obligatory} />
            )}
            {elective.size !== 0 && (
                <List
                    items={Array.from(elective.keys())}
                    className={styles.Elective}
                    renderItem={renderElectiveGroup}
                />
            )}
        </div>
    );
};
